import { toJpeg } from 'html-to-image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'
import { formatBirthdayByApi, formatCpf } from 'utils/formatters'

interface PeopleAssociation {
  status: 'active' | 'inactive' | 'pending' | 'expired'
  start_date: string | null
  end_date: string | null
}

interface MeResponse {
  success: boolean
  user?: {
    person?: {
      name: string
      cpf: string
      birthday: string
      peopleAssociations?: PeopleAssociation[]
    }
  }
}

export interface MembershipCardData {
  name: string
  cpf: string
  birthday: string
  validUntil: string
  issuedAt: string
}

const formatDate = (date?: string | null) => (date ? formatBirthdayByApi(date.slice(0, 10)) : '')

const CARD_WIDTH = 520
const CARD_HEIGHT = 330

export function useMembershipCard() {
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [card, setCard] = useState<MembershipCardData | null>(null)
  const frontRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let active = true

    const fetchCard = async () => {
      try {
        const { data } = await api.get<MeResponse>('/me?association_id=1')
        const person = data.user?.person
        const association = person?.peopleAssociations?.[0]

        if (active && data.success && person && association?.status === 'active' && association.end_date) {
          setCard({
            name: person.name,
            cpf: formatCpf(person.cpf),
            birthday: formatDate(person.birthday),
            validUntil: formatDate(association.end_date),
            issuedAt: formatDate(association.start_date)
          })
        }
      } catch {
        if (active) setCard(null)
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchCard()
    return () => {
      active = false
    }
  }, [])

  const handleDownload = useCallback(async () => {
    if (!frontRef.current || !backRef.current) return

    try {
      setDownloading(true)
      const pixelRatio = (3 * CARD_WIDTH) / frontRef.current.offsetWidth
      const options = { pixelRatio, cacheBust: true, quality: 0.92, backgroundColor: '#ffffff' }
      const [front, back] = await Promise.all([toJpeg(frontRef.current, options), toJpeg(backRef.current, options)])

      const { jsPDF: JsPdf } = await import('jspdf')
      const margin = 20
      const gap = 16
      const pageWidth = CARD_WIDTH + margin * 2
      const pageHeight = CARD_HEIGHT * 2 + gap + margin * 2
      const pdf = new JsPdf({
        unit: 'px',
        format: [pageWidth, pageHeight],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      })

      pdf.addImage(front, 'JPEG', margin, margin, CARD_WIDTH, CARD_HEIGHT)
      pdf.addImage(back, 'JPEG', margin, margin + CARD_HEIGHT + gap, CARD_WIDTH, CARD_HEIGHT)
      pdf.save('carteira-associado-aspaec.pdf')
    } catch {
      toast.error('Não foi possível gerar a carteirinha. Tente novamente.')
    } finally {
      setDownloading(false)
    }
  }, [])

  return { loading, downloading, card, frontRef, backRef, handleDownload }
}
